import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';

import { getIntakeFieldFormatError } from '../../fieldFormatValidation';
import {
  defaultFieldValue,
  hasDurableValue,
  requiresAffirmativeAcknowledgement,
  sameAsPracticeParentKey,
} from '../../fieldPersistence';
import {
  type SuggestionAcceptanceFailureKind,
  suggestionAcceptanceFailureKind,
} from '../../suggestionAcceptance';
import type {
  GeneratedIntakeField,
  GeneratedIntakeGroupHelp,
  IntakeFieldSuggestion,
  IntakeFieldValue,
} from '../../types';

import { GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFieldInput } from './IntakePageFieldInput';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { cardSx, groupTitleSx, wf } from './tokens';
import { useGroupHelpPanel } from './useGroupHelpPanel';

const EMPTY_GROUPS: GeneratedIntakeGroupHelp[] = [];
const EMPTY_SUGGESTIONS: Record<string, IntakeFieldSuggestion> = {};
const EMPTY_REASONS: Record<string, string> = {};
const EMPTY_ACKNOWLEDGED_VALUES: Record<string, boolean> = {};
const NO_HELP_ACTION = () => undefined;

function suggestionSourceLabel(suggestions: IntakeFieldSuggestion[]): string {
  const documentKinds = new Set(suggestions.map(suggestion => suggestion.document_kind));
  if (documentKinds.size !== 1) return 'Your uploaded documents';
  const [documentKind] = documentKinds;
  if (documentKind === 'W9') return 'Your W-9';
  return `Your ${documentKind}`;
}

function addressSectionHeading(field: GeneratedIntakeField): string | null {
  if (field.destination.kind !== 'intake_item_evidence') return null;
  const keys = [field.parent_field_key, field.destination.field_key, field.field_key].filter(
    Boolean,
  );
  if (keys.some(key => key.startsWith('billing_address'))) return 'Billing Address';
  if (keys.some(key => key.startsWith('mailing_address'))) return 'Mailing Address';
  return null;
}

interface IntakeFieldsPageProps {
  title: string;
  fields: GeneratedIntakeField[];
  groups?: GeneratedIntakeGroupHelp[];
  suggestions?: Record<string, IntakeFieldSuggestion>;
  onUseAllSuggestions?: (suggestions: IntakeFieldSuggestion[]) => Promise<void>;
  onAcceptSuggestion?: (suggestion: IntakeFieldSuggestion) => Promise<void>;
  onDiscardSuggestion?: (suggestion: IntakeFieldSuggestion) => void;
  onContinue: () => void;
  onBack?: () => void;
  nextLabel: string;
  draftValues: Record<string, IntakeFieldValue>;
  acknowledgedValues: Record<string, IntakeFieldValue>;
  hasAcknowledgedValues?: Record<string, boolean>;
  onDraftChange: (field: GeneratedIntakeField, value: IntakeFieldValue) => void;
  onPersistField: (field: GeneratedIntakeField, value: IntakeFieldValue) => Promise<void>;
  onHelpAction?: (action: 'message_specialist') => void;
  disabledReasons?: Record<string, string>;
  saveMode?: 'autosave' | 'explicit';
  isSaving?: boolean;
  saveErrorCount?: number;
  onSaveChanges?: () => void;
  embedded?: boolean;
  showRequiredErrors?: boolean;
  validationRequested?: boolean;
}

function hasFieldValue(value: IntakeFieldValue): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== null && value !== undefined;
}

/**
 * The generic entry page. Fields come from the generator, so this component is
 * correct for any generated page without being written per page. Exact-ID document
 * suggestions share the same durable typed-entry and persistence controls.
 */
export function IntakeFieldsPage({
  title,
  fields,
  groups: generatedGroups = EMPTY_GROUPS,
  suggestions = EMPTY_SUGGESTIONS,
  onUseAllSuggestions,
  onAcceptSuggestion,
  onDiscardSuggestion,
  onContinue,
  onBack,
  nextLabel,
  draftValues,
  acknowledgedValues,
  hasAcknowledgedValues = EMPTY_ACKNOWLEDGED_VALUES,
  onDraftChange,
  onPersistField,
  onHelpAction = NO_HELP_ACTION,
  disabledReasons = EMPTY_REASONS,
  saveMode = 'autosave',
  isSaving = false,
  saveErrorCount = 0,
  onSaveChanges,
  embedded = false,
  showRequiredErrors = false,
  validationRequested = false,
}: IntakeFieldsPageProps) {
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);
  const [acceptAllFailure, setAcceptAllFailure] = useState<SuggestionAcceptanceFailureKind | null>(
    null,
  );
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [suggestionSlotPage, setSuggestionSlotPage] = useState<string | null>(null);
  const onDraftChangeRef = useRef(onDraftChange);
  const onPersistFieldRef = useRef(onPersistField);
  const onAcceptSuggestionRef = useRef(onAcceptSuggestion);
  const onDiscardSuggestionRef = useRef(onDiscardSuggestion);
  onDraftChangeRef.current = onDraftChange;
  onPersistFieldRef.current = onPersistField;
  onAcceptSuggestionRef.current = onAcceptSuggestion;
  onDiscardSuggestionRef.current = onDiscardSuggestion;
  useEffect(() => setValidationAttempted(false), [title]);
  const handleFieldDraftChange = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) =>
      onDraftChangeRef.current(field, value),
    [],
  );
  const handleFieldPersist = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) =>
      onPersistFieldRef.current(field, value),
    [],
  );
  const handleAcceptSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) =>
      onAcceptSuggestionRef.current?.(suggestion) ?? Promise.resolve(),
    [],
  );
  const handleDiscardSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => onDiscardSuggestionRef.current?.(suggestion),
    [],
  );
  const nonFileFields = useMemo(
    () => fields.filter(field => field.data_type.toLowerCase() !== 'file'),
    [fields],
  );
  const visibleFields = useMemo(() => {
    const derivedAddressParentKeys = new Set(
      nonFileFields.flatMap(field => {
        const parentKey = sameAsPracticeParentKey(field);
        const key = field.field_id || field.field_key;
        const value = draftValues[key] ?? acknowledgedValues[key] ?? defaultFieldValue(field);
        return parentKey && value === true ? [parentKey] : [];
      }),
    );
    return nonFileFields.filter(field => !derivedAddressParentKeys.has(field.parent_field_key));
  }, [acknowledgedValues, draftValues, nonFileFields]);
  const fieldsByGroup = useMemo(
    () =>
      visibleFields.reduce<Record<string, GeneratedIntakeField[]>>((acc, field) => {
        const groupId = field.group_id || field.group_name || 'details';
        (acc[groupId] ??= []).push(field);
        return acc;
      }, {}),
    [visibleFields],
  );
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } =
    useGroupHelpPanel(generatedGroups);
  const drawerId = `intake-field-group-help-${useId().replace(/:/g, '')}`;

  const pendingSuggestionsOnPage = useMemo(
    () =>
      visibleFields.flatMap(field => {
        const suggestion = suggestions[field.field_id];
        const key = field.field_id || field.field_key;
        const currentValue =
          draftValues[key] ?? acknowledgedValues[key] ?? defaultFieldValue(field);
        return suggestion?.field_id === field.field_id &&
          JSON.stringify(currentValue) !== JSON.stringify(suggestion.value)
          ? [suggestion]
          : [];
      }),
    [acknowledgedValues, draftValues, suggestions, visibleFields],
  );
  const suggestedOnPage = useMemo(
    () =>
      pendingSuggestionsOnPage.filter(suggestion => {
        const field = visibleFields.find(candidate => candidate.field_id === suggestion.field_id);
        if (!field) return false;
        const key = field.field_id || field.field_key;
        const isDirty =
          JSON.stringify(draftValues[key]) !== JSON.stringify(acknowledgedValues[key]);
        return (
          !isDirty &&
          !hasAcknowledgedValues[key] &&
          !getIntakeFieldFormatError(field, suggestion.value, acknowledgedValues[key])
        );
      }),
    [
      acknowledgedValues,
      draftValues,
      hasAcknowledgedValues,
      pendingSuggestionsOnPage,
      visibleFields,
    ],
  );
  const protectedSuggestionCount = pendingSuggestionsOnPage.length - suggestedOnPage.length;
  useEffect(() => {
    setSuggestionSlotPage(previous => {
      if (pendingSuggestionsOnPage.length > 0) return title;
      return previous === title ? previous : null;
    });
  }, [pendingSuggestionsOnPage.length, title]);
  const showSuggestionSummary =
    Boolean(onUseAllSuggestions) &&
    (pendingSuggestionsOnPage.length > 0 || suggestionSlotPage === title);
  const acceptAll = async () => {
    if (!onUseAllSuggestions || isAcceptingAll) return;
    setAcceptAllFailure(null);
    setIsAcceptingAll(true);
    try {
      await onUseAllSuggestions(suggestedOnPage);
    } catch (error) {
      setAcceptAllFailure(suggestionAcceptanceFailureKind(error));
    } finally {
      setIsAcceptingAll(false);
    }
  };
  const dirtyFieldCount = visibleFields.filter(field => {
    const key = field.field_id || field.field_key;
    return JSON.stringify(draftValues[key]) !== JSON.stringify(acknowledgedValues[key]);
  }).length;
  const validationErrors = useMemo(
    () =>
      Object.fromEntries(
        visibleFields.flatMap(field => {
          const key = field.field_id || field.field_key;
          if (field.is_writable === false || disabledReasons[key]) return [];
          const value = draftValues[key] ?? acknowledgedValues[key] ?? defaultFieldValue(field);
          const error = getIntakeFieldFormatError(field, value, acknowledgedValues[key]);
          return error ? [[key, error]] : [];
        }),
      ),
    [acknowledgedValues, disabledReasons, draftValues, visibleFields],
  );
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const shouldShowValidation = validationAttempted || validationRequested;
  const missingAffirmativeAcknowledgement = visibleFields.find(field => {
    if (!requiresAffirmativeAcknowledgement(field)) return false;
    const key = field.field_id || field.field_key;
    const draftValue = draftValues[key] ?? acknowledgedValues[key];
    return draftValue !== true || acknowledgedValues[key] !== true;
  });
  const missingRequiredField = visibleFields.find(field => {
    if (!field.is_required || field.is_writable === false) return false;
    const key = field.field_id || field.field_key;
    if (disabledReasons[key]) return false;
    const value = draftValues[key] ?? acknowledgedValues[key] ?? defaultFieldValue(field);
    return !hasDurableValue(value);
  });
  const continueDisabledReason = missingAffirmativeAcknowledgement
    ? 'Acknowledge that the mailing address will be published before continuing.'
    : missingRequiredField
      ? 'Complete all required fields before continuing.'
      : shouldShowValidation && hasValidationErrors
        ? 'Correct the highlighted field formats before continuing.'
        : undefined;
  const validateThenContinue = () => {
    setValidationAttempted(true);
    if (!missingAffirmativeAcknowledgement && !missingRequiredField && !hasValidationErrors) {
      onContinue();
    }
  };
  const validateThenSave = () => {
    setValidationAttempted(true);
    if (!hasValidationErrors) onSaveChanges?.();
  };

  return (
    <>
      {!embedded && (
        <IntakePageHeader
          title={title}
          sub={
            fields.length === 0
              ? 'Nothing on this page is needed for your setup.'
              : 'Fill in the details below. Each field saves as you go.'
          }
        />
      )}

      {showSuggestionSummary && (
        <Box
          role="status"
          aria-label="Document suggestions"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            fontSize: '12px',
            color: pendingSuggestionsOnPage.length > 0 ? wf.ink : wf.muted,
            background: pendingSuggestionsOnPage.length > 0 ? wf.amberSoft : wf.surfaceAlt,
            border: `1px solid ${pendingSuggestionsOnPage.length > 0 ? wf.amber : wf.line}`,
            borderRadius: '6px',
            minHeight: '43px',
            padding: '8px 10px',
          }}
        >
          {pendingSuggestionsOnPage.length > 0 ? (
            <>
              <span>
                {suggestionSourceLabel(pendingSuggestionsOnPage)} found{' '}
                <strong>{pendingSuggestionsOnPage.length}</strong> suggestion
                {pendingSuggestionsOnPage.length === 1 ? '' : 's'} on this page. For safety, Accept
                all fills only empty fields
                {protectedSuggestionCount > 0
                  ? ` and leaves ${protectedSuggestionCount} existing ${
                      protectedSuggestionCount === 1 ? 'answer' : 'answers'
                    } unchanged.`
                  : '.'}
              </span>
              {suggestedOnPage.length > 0 && (
                <Button
                  aria-label="Accept all"
                  disabled={isAcceptingAll}
                  size="small"
                  variant="outlined"
                  onClick={() => void acceptAll()}
                  sx={{
                    flexShrink: 0,
                    backgroundColor: wf.surface,
                    color: wf.ink,
                    fontSize: '11px',
                    lineHeight: 1.4,
                    minWidth: 0,
                    padding: '3px 10px',
                    textTransform: 'none',
                  }}
                >
                  {isAcceptingAll ? 'Accepting…' : 'Accept all'}
                </Button>
              )}
            </>
          ) : (
            <span>No document suggestions to review on this page.</span>
          )}
        </Box>
      )}

      {acceptAllFailure && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '11px' }}>
          {acceptAllFailure === 'stale'
            ? 'Some suggestions changed before they could be accepted. Review the remaining fields and try again.'
            : "We couldn't confirm every suggestion. Review the refreshed fields and try again."}
        </Box>
      )}

      {Object.entries(fieldsByGroup).map(([groupId, groupFields]) => {
        const generatedGroup = generatedGroups.find(group => group.group_id === groupId);
        const groupName = generatedGroup?.group_name || groupFields[0]?.group_name || 'Details';
        return (
          <Box key={groupId} sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Box sx={groupTitleSx}>{groupName}</Box>
              {generatedGroup?.help_panel && (
                <GroupHelpTrigger
                  groupName={groupName}
                  controlsId={drawerId}
                  expanded={selectedGroupId === groupId}
                  onOpen={opener => openGroup(groupId, opener)}
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '10px',
              }}
            >
              {groupFields.map((field, index) => {
                const key = field.field_id || field.field_key;
                const sectionHeading = addressSectionHeading(field);
                const previousSectionHeading =
                  index > 0 ? addressSectionHeading(groupFields[index - 1]) : null;
                const acknowledgedValue =
                  acknowledgedValues[key] ?? (field.data_type === 'boolean' ? false : '');
                const draftValue = draftValues[key] ?? acknowledgedValue;
                const requiredValueMissing =
                  showRequiredErrors &&
                  field.is_required &&
                  (!hasFieldValue(draftValue) ||
                    (requiresAffirmativeAcknowledgement(field) && draftValue !== true));
                return (
                  <Fragment key={key}>
                    {sectionHeading && sectionHeading !== previousSectionHeading && (
                      <Box
                        component="h3"
                        sx={{
                          gridColumn: '1 / -1',
                          color: wf.ink,
                          fontSize: '12px',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          margin: '4px 0 0',
                        }}
                      >
                        {sectionHeading}
                      </Box>
                    )}
                    <IntakePageFieldInput
                      field={field}
                      draftValue={draftValue}
                      acknowledgedValue={acknowledgedValue}
                      hasAcknowledgedValue={hasAcknowledgedValues[key] ?? false}
                      onDraftChange={handleFieldDraftChange}
                      onPersist={handleFieldPersist}
                      onAcceptSuggestion={onAcceptSuggestion ? handleAcceptSuggestion : undefined}
                      onDiscardSuggestion={
                        onDiscardSuggestion ? handleDiscardSuggestion : undefined
                      }
                      suggestion={
                        suggestions[field.field_id]?.field_id === field.field_id
                          ? suggestions[field.field_id]
                          : undefined
                      }
                      disabled={isAcceptingAll || Boolean(disabledReasons[key])}
                      disabledReason={disabledReasons[key]}
                      saveMode={saveMode}
                      validationError={
                        requiredValueMissing
                          ? `${field.label} is required.`
                          : shouldShowValidation
                            ? validationErrors[key]
                            : undefined
                      }
                    />
                  </Fragment>
                );
              })}
            </Box>
          </Box>
        );
      })}

      {visibleFields.length === 0 && (
        <Box sx={{ ...cardSx, background: wf.surfaceAlt, color: wf.muted, fontSize: '12.5px' }}>
          This section is greyed out in the sidebar because your answers made it unnecessary.
        </Box>
      )}

      {saveErrorCount > 0 && (
        <Box sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {saveErrorCount} field{saveErrorCount === 1 ? '' : 's'} could not be saved. Review and
          retry the remaining changes.
        </Box>
      )}

      {!embedded && (
        <IntakePageFooter
          primaryLabel={nextLabel}
          onPrimary={validateThenContinue}
          onBack={onBack}
          saveLabel={saveMode === 'explicit' ? (isSaving ? 'Saving…' : 'Save changes') : undefined}
          onSave={validateThenSave}
          saveDisabled={
            isSaving || dirtyFieldCount === 0 || (shouldShowValidation && hasValidationErrors)
          }
          disabled={
            Boolean(missingAffirmativeAcknowledgement) ||
            Boolean(missingRequiredField) ||
            (shouldShowValidation && hasValidationErrors)
          }
          disabledReason={continueDisabledReason}
        />
      )}
      {embedded && saveMode === 'explicit' && onSaveChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            disabled={
              isSaving || dirtyFieldCount === 0 || (shouldShowValidation && hasValidationErrors)
            }
            onClick={validateThenSave}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      )}
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
    </>
  );
}
